import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarDays, Clock, MapPin, Mountain, ShieldAlert, Sun, Footprints, ArrowRight, Compass, MessageCircle } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import ErtigaSection from "@/components/ErtigaSection";
import DestinationCard from "@/components/DestinationCard";
import { useEnquiry } from "@/components/Enquiry";
import { DESTINATIONS, DESTINATION_BY_SLUG, ACCESS_LABEL, ACCESS_HINT, seoFor } from "@/data/destinations";
import { IMG, img } from "@/data/images";
import { BUSINESS, waLink } from "@/data/business";

export default function DestinationPage() {
  const { slug } = useParams();
  const d = DESTINATION_BY_SLUG[slug];
  const { openEnquiry } = useEnquiry();

  if (!d) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 text-center">
        <Seo title="Destination not found | Tingadya Travels" description="The destination you're looking for isn't on our guide yet." />
        <h1 className="font-display text-3xl text-white">This destination isn't on our map yet</h1>
        <Link to="/explore" data-testid="notfound-back-to-explore" className="btn-gold">Explore all destinations</Link>
      </div>
    );
  }

  const seo = seoFor(d);
  const gallery = d.images.slice(1, 3);
  const related = DESTINATIONS.filter((x) => x.slug !== d.slug && (x.categories || []).some((c) => (d.categories || []).includes(c))).slice(0, 4);
  const waMsg = `Hello Tingadya Travels! I'd like to plan a trip to ${d.name}. Please share taxi options and prices.`;

  const infoCards = [
    { icon: MapPin, title: "Nearest road access", body: d.accessNote ? `${d.base} — ${d.accessNote}` : `Motorable to ${d.name} via ${d.base}.` },
    { icon: Clock, title: "Suggested duration (estimate)", body: d.duration },
    { icon: Sun, title: "Best season", body: d.season },
    { icon: ShieldAlert, title: "Trek, permits & access", body: d.permits || "No special permits needed — just carry a valid photo ID and check weather before departure." },
  ].filter((c) => c.body);

  return (
    <>
      <Seo title={seo.title} description={seo.description} />
      {/* Hero */}
      <section className="relative flex min-h-[68vh] items-end overflow-hidden sm:min-h-[78vh]" data-testid="destination-hero">
        <motion.img
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
          src={img(d.images[0], 1800)}
          alt={`${d.name}, ${d.region} — Uttarakhand`}
          className="absolute inset-0 h-full w-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/20" />
        <div className="container-x relative z-10 pb-14 pt-32">
          <Link to="/explore" data-testid="destination-back-link" className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-snow/80 transition-colors hover:text-gold">
            <ArrowLeft size={16} /> All destinations
          </Link>
          <div className="flex flex-wrap gap-2">
            <span className="chip border-gold/40 bg-black/50 text-gold">{d.region}</span>
            <span className="chip bg-black/50">{ACCESS_LABEL[d.access] || "Drive"}</span>
            {d.near && d.near.length > 0 && <span className="chip bg-black/50">Near {d.near.map((n) => n.charAt(0).toUpperCase() + n.slice(1)).join(" & ")}</span>}
          </div>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold text-white sm:text-5xl lg:text-6xl" data-testid="destination-title">{d.name}</h1>
        </div>
      </section>

      {/* Body */}
      <section className="section-pad" data-testid="destination-body">
        <div className="container-x grid gap-14 lg:grid-cols-[1fr_360px]">
          <div>
            <p className="eyebrow">{d.base}</p>
            <p className="mt-5 text-lg leading-relaxed text-snow/90" data-testid="destination-description">{d.description}</p>

            {(d.attractions.length > 0 || d.activities.length > 0) && (
              <div className="mt-10 grid gap-6 sm:grid-cols-2">
                {d.attractions.length > 0 && (
                  <div className="rounded-2xl border border-white/10 bg-coal p-6" data-testid="destination-attractions">
                    <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-gold"><Compass size={18} /> Main attractions</h2>
                    <ul className="mt-4 flex flex-col gap-2.5 text-sm text-mist">
                      {d.attractions.map((a) => (
                        <li key={a} className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />{a}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {d.activities.length > 0 && (
                  <div className="rounded-2xl border border-white/10 bg-coal p-6" data-testid="destination-activities">
                    <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-gold"><Footprints size={18} /> Recommended activities</h2>
                    <ul className="mt-4 flex flex-col gap-2.5 text-sm text-mist">
                      {d.activities.map((a) => (
                        <li key={a} className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />{a}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              {infoCards.map((c) => (
                <div key={c.title} className="rounded-2xl border border-white/10 bg-coal/70 p-6" data-testid={`destination-info-${c.title.slice(0, 12).toLowerCase().replace(/\s+/g, "-")}`}>
                  <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gold"><c.icon size={14} /> {c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-mist">{c.body}</p>
                </div>
              ))}
            </div>

            {d.startFrom && d.startFrom.length > 0 && (
              <div className="mt-6 rounded-2xl border border-white/10 bg-coal/70 p-6" data-testid="destination-startfrom">
                <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gold"><Mountain size={14} /> Suggested trip starts</h3>
                <p className="mt-3 text-sm leading-relaxed text-mist">
                  Common routes begin from {d.startFrom.join(", ")}. We'll shape the drive, night halts and pickups around your city and dates — road conditions, weather and travel time are confirmed before departure.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {d.startFrom.map((s) => <span key={s} className="chip border-gold/30 text-gold">{s}</span>)}
                </div>
              </div>
            )}

            {/* Gallery */}
            {gallery.length > 0 && (
              <div className="mt-12" data-testid="destination-gallery">
                <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">Photo gallery</h2>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  {gallery.map((g, i) => (
                    <figure key={g} className={`group overflow-hidden rounded-2xl border border-white/10 ${i === 0 ? "sm:col-span-2 aspect-[16/8]" : "aspect-[4/3]"}`}>
                      <img src={img(g, i === 0 ? 1400 : 900)} alt={`${d.name} — Uttarakhand travel photograph ${i + 2}`} loading="lazy" className="img-zoom h-full w-full object-cover" />
                      <figcaption className="sr-only">{d.name}, {d.region}, Uttarakhand</figcaption>
                    </figure>
                  ))}
                </div>
                {d.imgNote && <p className="mt-3 text-xs italic text-slategray" data-testid="destination-image-note">Note: {d.imgNote}. No location is misrepresented — ask us for on-ground photos before you travel.</p>}
              </div>
            )}
          </div>

          {/* Sticky enquiry card */}
          <aside className="lg:sticky lg:top-28 lg:h-fit">
            <div className="gold-ring rounded-3xl border border-gold/40 bg-coal p-7" data-testid="destination-enquiry-card">
              <p className="eyebrow">Travel with Tingadya Travels</p>
              <h2 className="mt-2 font-display text-2xl font-bold text-white">Plan a Trip to {d.short || d.name}</h2>
              <p className="mt-3 text-sm leading-relaxed text-mist">
                Taxi in our {BUSINESS.vehicle}, routes and night halts planned by locals, and honest season advice before you commit.
              </p>
              <div className="mt-6 flex flex-col gap-3">
                <button onClick={() => openEnquiry(d.name, `Destination page: ${d.slug}`)} data-testid="get-taxi-price-button" className="btn-gold w-full">Get Taxi Price <ArrowRight size={16} /></button>
                <button onClick={() => openEnquiry(d.name, `Destination page: ${d.slug}`)} data-testid="plan-trip-destination-button" className="btn-ghost-gold w-full">Plan a Trip to This Destination</button>
                <a href={waLink(waMsg)} target="_blank" rel="noopener noreferrer" data-testid="destination-whatsapp-button" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-ink transition-transform hover:scale-[1.02]">
                  WhatsApp {d.short || d.name} Enquiry
                </a>
              </div>
              <div className="mt-6 flex items-center gap-3 rounded-2xl border border-white/10 bg-black/30 p-4">
                <CalendarDays size={18} className="shrink-0 text-gold" />
                <p className="text-xs leading-relaxed text-mist">Seasonal caveats apply — road conditions, weather, travel duration and opening dates can change any plan. We reconfirm everything before departure.</p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <ErtigaSection destination={d.name} compact />

      {/* Related */}
      {related.length > 0 && (
        <section className="section-pad border-t border-white/5" data-testid="destination-related">
          <div className="container-x">
            <div className="flex items-end justify-between gap-6">
              <div>
                <p className="eyebrow">Keep Exploring</p>
                <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">You May Also Love</h2>
              </div>
              <Link to="/explore" data-testid="destination-related-view-all" className="btn-ghost-gold hidden shrink-0 sm:inline-flex">View All Destinations</Link>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((r) => <DestinationCard key={r.slug} d={r} testid={`related-card-${r.slug}`} />)}
            </div>
          </div>
        </section>
      )}
      <div className="h-24 lg:hidden" aria-hidden="true" />

      {/* Mobile sticky enquiry bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gold/30 bg-ink/95 px-3 pt-3 backdrop-blur-xl lg:hidden" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }} data-testid="destination-mobile-cta-bar">
        <div className="flex items-center gap-2">
          <button onClick={() => openEnquiry(d.name, `Mobile CTA: ${d.slug}`)} data-testid="destination-mobile-get-price" className="flex-1 rounded-full bg-gold px-6 py-3 text-center text-sm font-bold text-ink transition-all hover:bg-gold-light active:scale-95">
            Get Taxi Price
          </button>
          <a href={waLink(waMsg)} target="_blank" rel="noopener noreferrer" data-testid="destination-mobile-whatsapp" className="shrink-0 rounded-full border border-gold/40 bg-gold/5 px-5 py-3 text-sm font-semibold text-gold transition-colors hover:bg-gold/15">
            <MessageCircle size={16} className="inline align-[-2px]" /> WhatsApp
          </a>
        </div>
      </div>
    </>
  );
}
